/* ログ一覧画面 */
import React, { useState, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { Button } from 'react-bootstrap';
import LogResultList from '../organisms/LogResultList';
import LogSearchForm from '../organisms/LogSearchForm';
import LoadingModal from '../molecules/LoadingModal';
import { Props, SearchParams } from '../types/LogList';
import axios from '../axios';
import { handleBrowzerBack } from '../BrowzerBack';
import { log_search_params_init } from '../Const';
import { parse } from 'querystring';
import { jumpUrl } from './../Common';
import Moment from 'moment';

const LogList: React.FC = () => {
  const [log_list, setLogList] = useState([]);
  const [config, setConfig] = useState<Props['config']>({
    display_list_number: [],
  });
  const [options, setOptions] = useState<Props['options']>();
  const [messages, setMessages] = useState([]);
  // 検索パラメータの初期化
  const [search_params, setSearchParams] = useState(log_search_params_init);
  // 最新の検索実行時のパラメータ
  const [last_search_params, setLastSearchParams] = useState(log_search_params_init);
  const [is_loading, setIsLoading] = useState(false);
  // Ajaxリクエスト中フラグ
  const [is_connecting, setIsConnecting] = useState(false);

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowzerBack();

    /**
     * ログを検索する
     * @param params 検索パラメータ
     */
    const searchLogs = async (params: SearchParams): Promise<void> => {
      try {
        //ローディング画面表示
        setIsLoading(true);
        const response = await axios.post('/staff/log_list/search', params);
        setLogList(response.data.log_list);
        setConfig(response.data.config);
        setOptions(response.data.options);
      } catch (error) {
        console.error(error.response.data.message);
      } finally {
        //ローディング画面非表示
        setIsLoading(false);
      }
    };

    // 検索パラメータの初期値を設定
    const new_params = Object.assign({}, search_params);
    // 開始日時セット
    const start_date = new Date();
    start_date.setDate(start_date.getDate() - 3);
    new_params.created_at_from_ymd =
      start_date.getFullYear() + '-' + (start_date.getMonth() + 1) + '-' + start_date.getDate();
    new_params.created_at_from_hour = ('0' + start_date.getHours()).slice(-2);
    new_params.created_at_from_minute = ('0' + start_date.getMinutes()).slice(-2);
    // 終了日時セット
    const end_date = new Date();
    new_params.created_at_to_ymd =
      end_date.getFullYear() + '-' + (end_date.getMonth() + 1) + '-' + end_date.getDate();
    new_params.created_at_to_hour = ('0' + end_date.getHours()).slice(-2);
    new_params.created_at_to_minute = ('0' + end_date.getMinutes()).slice(-2);

    setSearchParams(new_params);
    setLastSearchParams(new_params);
    if (location.search.length > 0) {
      // getパラメータをパースする
      const get_queries = parse(location.search.slice(1));
      // 検索パラメータの初期化状態にマージする
      Object.assign(new_params, get_queries);
      // パラメータを更新する
      setSearchParams(new_params);
      setLastSearchParams(new_params);
    }

    // Ajaxリクエスト
    searchLogs(new_params);
  }, []);

  // 検索処理
  const handleClickSearch = useCallback(() => {
    // クリックイベント
    const searchLogList = async () => {
      try {
        //ローディング画面表示
        setIsLoading(true);
        setIsConnecting(true);
        setMessages([]);
        const response = await axios.post('/staff/log_list/search', search_params);
        setLogList(response.data.log_list);
        setLastSearchParams(search_params);
      } catch (exception) {
        console.error(exception);
        setMessages(exception.response.data.message);
        jumpUrl(exception.response.data.url);
      } finally {
        setIsConnecting(false);
        //ローディング画面非表示
        setIsLoading(false);
      }
    };
    searchLogList();
  }, [search_params]);

  //
  const handleChangeSearchParams = useCallback(
    (target: string | string[], param: string | Date) => {
      let set_param = '';
      const params = Object.assign({}, search_params) as SearchParams;

      const set_targets: string[] = [];

      // targetが単一stringであるかをチェックする
      if (typeof target === 'string') {
        set_targets.push(target);
      } else {
        target.map((param) => set_targets.push(param));
      }

      // targetの個数分だけ繰り返しパラメータをセットする
      set_targets.map((target_param) => {
        if (Object.prototype.toString.call(param).slice(8, -1).toLowerCase() === 'date') {
          // Date型
          set_param = Moment(param).format('YYYY-MM-DD');
        } else if (typeof param === 'string') {
          // string型
          set_param = param;
        }

        const key: string = target_param as string; // targetの型を明示的に指定
        params[key] = set_param;
      });

      setSearchParams(params);
    },
    [search_params]
  );

  /**
   * 戻るボタンをクリックする
   */
  const handleClickReturn = () => {
    location.replace(`${location.origin}${process.env.MIX_MOCK_URL}/staff/list`);
  };

  return (
    <div className="container">
      <LogSearchForm
        options={options}
        search_params={search_params}
        message={messages}
        handleClickSearch={handleClickSearch}
        handleChangeSearchParams={handleChangeSearchParams}
        is_connecting={is_connecting}
      />
      <LogResultList projects={log_list} config={config} />
      <div className="mt-4 text-center">
        <Button
          variant="main-color"
          className="return-button"
          size="lg"
          onClick={handleClickReturn}
        >
          戻る
        </Button>
      </div>
      {/* ローディング画面 */}
      <LoadingModal show={is_loading} />
    </div>
  );
};

const container = document.getElementById('log-list');
if (container) {
  const root = createRoot(container);
  root.render(<LogList />);
}
